import { createContext, useEffect, useMemo, useState } from 'react';
import { subscribeAuth, signInWithGoogle, signOut } from '../services/firebase';

export const AuthContext = createContext({
  user: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const unsub = subscribeAuth(async (u) => {
      if (!u) {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }
      // Aguarda o token estar realmente disponível antes de expor o user.
      // Sem isso, consumidores podem disparar fetch antes do JWT cachear
      // e o backend recebe a request sem Authorization.
      try {
        await u.getIdToken();
        if (cancelled) return;
        setUser(u);
      } catch (e) {
        if (cancelled) return;
        console.warn('[auth] getIdToken falhou após sign-in:', e?.message || e);
        setUser(null);
        setError(e?.message || 'Falha ao obter token.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      signIn: async () => {
        setError(null);
        try {
          await signInWithGoogle();
        } catch (e) {
          setError(e.message || 'Falha no login.');
          throw e;
        }
      },
      signOut: async () => {
        setError(null);
        await signOut();
      },
    }),
    [user, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
